/**
 * @cfleet/auth - Cross-tab session channel
 *
 * A dispatcher commonly runs the web app in several tabs/windows at once. Each
 * tab has its own in-memory auth state (React state + the api-client
 * MemoryTokenStorage), so without coordination they drift apart: one tab
 * rotates the refresh token while the others keep holding the old one (which
 * the backend may then treat as reuse), or one tab logs out while the rest stay
 * authenticated.
 *
 * `localStorage` is shared across same-origin tabs, so the *persisted* session
 * is already coherent. What is missing is propagating two events between the
 * live JS contexts:
 *   - `tokens`  — this tab rotated the access/refresh token; others should adopt
 *                 it instead of each running their own refresh.
 *   - `logout`  — an authoritative sign-out (manual logout or a server 401);
 *                 every tab should clear.
 *
 * Transport: `BroadcastChannel` when available (all evergreen browsers), with a
 * `storage`-event fallback for the rare environment without it. Both are
 * no-ops under SSR / React Native (no `window`), so importing this from shared
 * code is safe. BroadcastChannel never echoes a message back to the sender, and
 * the storage fallback ignores its own writes, so a tab never reacts to itself.
 */

/** Name of the BroadcastChannel shared by every tab of the same origin. */
export const SESSION_CHANNEL_NAME = 'cfleet.auth.session';

/** localStorage key used by the storage-event fallback transport. */
export const SESSION_CHANNEL_FALLBACK_KEY = 'cfleet.auth.session-broadcast';

/**
 * A coherence event shared between tabs.
 * - `tokens` — adopt these freshly rotated tokens (in-memory only; storage is
 *   already shared and was written by the originating tab).
 * - `logout` — clear all auth state; the session is authoritatively over.
 */
export type SessionMessage =
  | { type: 'tokens'; accessToken: string; refreshToken: string; expiresIn: number }
  | { type: 'logout' };

/** Runtime guard so a malformed/foreign payload never reaches consumers. */
function isSessionMessage(value: unknown): value is SessionMessage {
  if (!value || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  if (msg.type === 'logout') return true;
  if (msg.type === 'tokens') {
    return (
      typeof msg.accessToken === 'string' &&
      typeof msg.refreshToken === 'string' &&
      typeof msg.expiresIn === 'number'
    );
  }
  return false;
}

/** Transport-agnostic channel surface. */
interface ChannelImpl {
  post(msg: SessionMessage): void;
  close(): void;
}

// Subscribers live at module scope so a single transport feeds every consumer
// in this tab (AuthProvider subscribes once; tests may add their own).
const handlers = new Set<(msg: SessionMessage) => void>();

/** Fan an inbound message out to subscribers; one throwing listener must not
 * starve the others. */
function dispatch(msg: SessionMessage): void {
  for (const handler of handlers) {
    try {
      handler(msg);
    } catch {
      // A consumer's failure is its own problem — keep delivering.
    }
  }
}

/** Preferred transport. Returns null when BroadcastChannel is unavailable or
 * construction throws (some locked-down environments). */
function createBroadcastImpl(): ChannelImpl | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
  } catch {
    return null;
  }
  channel.onmessage = (event: MessageEvent) => {
    if (isSessionMessage(event.data)) dispatch(event.data);
  };
  return {
    post: (msg) => {
      try {
        channel.postMessage(msg);
      } catch {
        // Channel closed underneath us — nothing actionable.
      }
    },
    close: () => channel.close(),
  };
}

/** Fallback transport: write-then-remove a localStorage key so other tabs get a
 * `storage` event. The write fires in other tabs only, never the originator, so
 * there is no self-echo. A nonce guarantees back-to-back identical messages
 * still register as a change. */
function createStorageImpl(): ChannelImpl | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  const onStorage = (event: StorageEvent): void => {
    if (event.key !== SESSION_CHANNEL_FALLBACK_KEY || event.newValue == null) return;
    try {
      const envelope = JSON.parse(event.newValue) as { msg?: unknown };
      if (isSessionMessage(envelope.msg)) dispatch(envelope.msg);
    } catch {
      // Ignore malformed payloads.
    }
  };

  window.addEventListener('storage', onStorage);

  return {
    post: (msg) => {
      try {
        const envelope = JSON.stringify({ msg, nonce: `${Date.now()}-${Math.random()}` });
        window.localStorage.setItem(SESSION_CHANNEL_FALLBACK_KEY, envelope);
        // Remove immediately; the value is a signal, not state to persist.
        window.localStorage.removeItem(SESSION_CHANNEL_FALLBACK_KEY);
      } catch {
        // Storage disabled (private mode / quota) — drop the message.
      }
    },
    close: () => window.removeEventListener('storage', onStorage),
  };
}

// `undefined` = not yet initialized; `null` = initialized with no usable
// transport (SSR / native), in which case post + subscribe are inert.
let impl: ChannelImpl | null | undefined;

function getImpl(): ChannelImpl | null {
  if (impl === undefined) {
    impl = createBroadcastImpl() ?? createStorageImpl() ?? null;
  }
  return impl;
}

/**
 * Broadcast a session event to every other tab of this origin. No-op when no
 * cross-tab transport is available (SSR / React Native).
 */
export function postSessionMessage(msg: SessionMessage): void {
  getImpl()?.post(msg);
}

/**
 * Subscribe to session events from other tabs. Returns an unsubscribe function.
 * Safe to call in any environment; the handler simply never fires where no
 * transport exists.
 */
export function subscribeSessionMessages(handler: (msg: SessionMessage) => void): () => void {
  getImpl(); // ensure the transport is listening
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

/**
 * Tear down the transport and drop all subscribers. Primarily a test helper so
 * the module-level singleton does not leak between cases.
 */
export function closeSessionChannel(): void {
  impl?.close();
  impl = undefined;
  handlers.clear();
}
